/*
 ************************************************************************
 *  © [2015 - 2023] Quintype Technologies India Private Limited
 *  All Rights Reserved.
 *************************************************************************
 */

import { findElementNP } from "pages/story-editor/operations/find";
import { addIfNotExists } from "utils/array.utils";
import { setTextSelectionToDocumentEnd } from "pages/story-editor/operations/selection";
import { CardId } from "api/primitive-types";
import { AnyStory, StoryElement, CompositeStoryElement, ChildStoryElement } from "api/story";
import { EditorState } from "prosemirror-state";

function move(arr: Array<any>, from: number, to: number) {
  let array = arr.slice();

  let element = array[from];
  array[from] = array[to];
  array[to] = element;

  return array;
}

function updateUpdatedCard(updatedCards: Array<String>, cardId: CardId) {
  return cardId ? addIfNotExists(updatedCards, cardId) : updatedCards;
}

function moveStoryElementDownWithinCompositeElement(
  story: AnyStory,
  parentStoryElement: CompositeStoryElement,
  elementIndex: number
): AnyStory {
  const updatedCards = updateUpdatedCard(story["updated-cards"], parentStoryElement["card-id"]);
  const parentStoryElementTree = parentStoryElement.tree;

  return {
    ...story,
    "story-elements": {
      ...story["story-elements"],
      [parentStoryElement.id]: {
        ...story["story-elements"][parentStoryElement.id],
        tree: move(parentStoryElementTree, elementIndex, elementIndex + 1)
      }
    },
    "updated-cards": updatedCards
  };
}

function moveStoryElementDownWithinCompositeElementNode(
  editorState: EditorState,
  storyElement: StoryElement | CompositeStoryElement | ChildStoryElement,
  nextStoryElement: StoryElement | CompositeStoryElement | ChildStoryElement
): EditorState {
  const tr = editorState.tr;
  const elementNP = findElementNP(editorState, storyElement);
  const nextElementNP = findElementNP(editorState, nextStoryElement);

  if (elementNP && nextElementNP) {
    const elementNode = elementNP.node;
    tr.insert(nextElementNP.pos + nextElementNP.node.nodeSize, elementNode);
    tr.delete(elementNP.pos, elementNP.pos + elementNode.nodeSize);
    const trWithFocus = setTextSelectionToDocumentEnd(tr, editorState);
    return editorState.apply(trWithFocus);
  }
  return editorState;
}

function moveStoryElementDownInCompositeElements(
  story: AnyStory,
  editorState: EditorState,
  storyElement: ChildStoryElement,
  parentStoryElement: CompositeStoryElement
): {
  story: AnyStory;
  editorState: EditorState;
} {
  const parentStoryElementTree = parentStoryElement.tree;

  const currentStoryElementIndex = parentStoryElementTree.findIndex(
    (storyElementId) => storyElementId === storyElement.id
  );

  const nextStoryElementId = parentStoryElementTree[currentStoryElementIndex + 1];
  const nextStoryElement = story["story-elements"][nextStoryElementId];

  const newStory = moveStoryElementDownWithinCompositeElement(story, parentStoryElement, currentStoryElementIndex);
  const newEditorState = moveStoryElementDownWithinCompositeElementNode(editorState, storyElement, nextStoryElement);

  return { story: newStory, editorState: newEditorState };
}

export default moveStoryElementDownInCompositeElements;
